/**
 * Page: EditTemplate
 * Admin-only page to edit an existing creative template. Verifies admin access,
 * fetches template data, formats it for the form, handles submission with
 * robust error handling and an error boundary around the form.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TemplateForm from '../../../components/admin/createTemplates/TemplateForm';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { auth } from '../../../firebase/firebase';
import { SERVER_URL } from '../../../api/core';
import { useAuth } from '../../../context/auth';
import { ArrowLeft } from 'lucide-react';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { Button } from '../../../components/ui/buttons/button';
import { Input } from '../../../components/ui/inputs/input';
import { Label } from '../../../components/ui/inputs/label';
import { Textarea } from '../../../components/ui/inputs/textarea';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

// Create an error boundary wrapper component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Form submission error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI when an error occurs
      return (
        <div className="p-4 bg-red-50 rounded-md dark:bg-red-900/20">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-300">Something went wrong</h3>
          <p className="mt-2 text-sm text-red-700 dark:text-red-400">
            {this.state.error?.message || "An unexpected error occurred. Please try again."}
          </p>
          <button 
            className="mt-3 px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const EditTemplate = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [template, setTemplate] = useState(null);
  
  // Check if user is the admin and fetch template data
  useEffect(() => {
    const checkAdminAndFetchTemplate = async () => {
      setIsLoading(true);
      
      try {
        // Check if user is an admin using the same pattern as Admin.jsx
        if (user) {
          const userIsAdmin = isUserAdmin(user);
          
          if (userIsAdmin) {
            setIsAuthorized(true);
            
            // Fetch template data
            try {
              const idToken = await auth.currentUser.getIdToken();
              const response = await fetch(`${SERVER_URL}/templateManagement/${templateId}`, {
                headers: {
                  Authorization: `Bearer ${idToken}`,
                },
              });
              
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to fetch template');
              }
              
              const templateData = await response.json();
              setTemplate(templateData);
            } catch (templateError) {
              addToast('Failed to load template data', 'error');
              navigate('/app/private/manageTemplates');
              return;
            }
          } else {
            // Redirect unauthorized users
            navigate('/app');
          }
        } else if (user === null) {
          // No user, redirect to login
          navigate('/login');
        }
      } catch (error) {
        // Redirect on error
        navigate('/app');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAdminAndFetchTemplate();
  }, [navigate, user, templateId, addToast]);
  
  // Reset the page when unmounting to prevent stale state
  useEffect(() => {
    return () => {
      setIsSubmitting(false);
      setSubmitError('');
      setSuccessMessage('');
      setIsSuccessful(false);
    };
  }, []);

  // Auto-dismiss error and success messages after 3 seconds
  useEffect(() => {
    if (submitError || successMessage) {
      const timer = setTimeout(() => {
        if (submitError) setSubmitError('');
        if (successMessage) setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitError, successMessage]);

  // When successful, navigate after a delay
  useEffect(() => {
    let timer;
    if (isSuccessful) {
      timer = setTimeout(() => {
        navigate('/app/private/manageTemplates');
      }, 1500);
    }
    return () => clearTimeout(timer);
  }, [isSuccessful, navigate]);
  
  // Format template data for the form
  const formatTemplateForForm = () => {
    if (!template) return null;
    
    // Tags should be passed as an array, not a string
    const tagsArray = template.tags || [];
    
    // Format date
    let formattedDate = '';
    try {
      if (template.datePublished?._seconds) {
        // Handle Firestore timestamp object
        const date = new Date(template.datePublished._seconds * 1000);
        formattedDate = date.toISOString().split('T')[0];
      } else if (template.datePublished?.toDate) {
        // Handle Firestore timestamp
        const date = template.datePublished.toDate();
        formattedDate = date.toISOString().split('T')[0];
      } else if (template.datePublished) {
        // Handle regular date
        const date = new Date(template.datePublished);
        formattedDate = date.toISOString().split('T')[0];
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      formattedDate = new Date().toISOString().split('T')[0]; // Fallback to today
    }
    
    // Create preview image object with url and name properties
    const previewImage = template.previewImageURL ? {
      url: template.previewImageURL,
      name: `${template.company} Preview Image`,
      isExistingImage: true
    } : null;
    
    return {
      company: template.company,
      canvaLink: template.canvaLink,
      tags: tagsArray,
      keywords: template.keywords || [],
      searchableText: template.searchableText || '',
      landingPageURL: template.landingPageURL,
      adCopy: template.adCopy,
      type: template.type || template.size, // Handle both type and legacy size fields
      datePublished: formattedDate,
      previewImage
    };
  };

  // Use useCallback to avoid recreating this function on each render
  const handleSubmit = useCallback(async (formData) => {
    // Safety first - make sure we're not already submitting
    if (isSubmitting) {
      console.log('Already submitting, ignoring request');
      return;
    }
    
    // Reset state for this submission
    setSubmitError('');
    setIsSubmitting(true);
    
    let response, data;
    
    // Wrap everything in a try-catch with no exceptions
    try {
      console.log('Starting update process...');
      
      // Get the authentication token
      let idToken;
      try {
        idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!idToken) {
          throw new Error('Not authenticated. Please log in again.');
        }
        console.log('Auth token acquired successfully');
      } catch (authError) {
        console.error('Authentication error:', authError);
        setSubmitError('Authentication error: ' + (authError.message || 'Failed to get authentication token'));
        setIsSubmitting(false);
        return; // Stop but stay on this page
      }
      
      // Log the form data for debugging
      console.log('Form data before submission:');
      for (const pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }
      
      // Make the API request with authentication header
      try {
        console.log('Submitting to server...');
        console.log('Using type field instead of size for template categorization');
        response = await fetch(`${SERVER_URL}/templateManagement/update/${templateId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${idToken}`
            // Don't specify Content-Type for FormData - browser will set it automatically
          },
          body: formData,
          // CORS issue fix: DO NOT include credentials - this conflicts with wildcard origin
        });
        console.log('Server response received:', response.status);
      } catch (fetchError) {
        console.error('Network error:', fetchError);
        setSubmitError('Network error: Could not connect to server. Please check your connection.');
        setIsSubmitting(false);
        return; // Stop but stay on this page
      }

      // Try to parse the response
      try {
        if (response.status === 204 || response.headers.get('content-length') === '0') {
          data = { success: true };
        } else {
          data = await response.json();
        }
        console.log('Response data parsed:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        setSubmitError(`Failed to parse server response (Status: ${response.status}). The server may be experiencing issues.`);
        setIsSubmitting(false);
        return; // Stop but stay on this page
      }
      
      // Check for response status issues
      if (!response.ok) {
        console.error('Server error response:', response.status, data);
        const errorMessage = data.error || `Failed to update template (Status: ${response.status})`;
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return; // Stop but stay on this page
      }

      // Success case - only execute if we got here
      console.log('Update successful!');
      
      // Show toast notification
      addToast('Template updated successfully!', 'success');
      
      // Navigate immediately to the manage templates page
      navigate('/app/private/manageTemplates');
      
    } catch (error) {
      // Catch-all for any other errors
      setSubmitError(error.message || 'An unexpected error occurred');
      setIsSubmitting(false);
    }
  }, [isSubmitting, templateId, addToast, navigate]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <span className="mt-4 text-gray-600 dark:text-gray-300">Loading template data...</span>
          </div>
        </div>
      </div>
    );
  }
  
  // If not authorized, redirect happens in the useEffect
  if (!isAuthorized || !template) {
    return null;
  }
  
  // Create initial form data for the template form
  const initialFormData = formatTemplateForForm();

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <div className="flex items-center mb-4">
            {/* Back button */}
            <button
              onClick={() => navigate('/app/private/manageTemplates')}
              className="mr-3 flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back to Manage Templates
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Template</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Update the template information below.
          </p>
        </div>
      </div>
      
      {submitError && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {submitError}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm p-5 border border-gray-200 dark:border-gray-700">
        <ErrorBoundary>
          <TemplateForm 
            onSubmit={handleSubmit} 
            isSubmitting={isSubmitting} 
            initialData={initialFormData}
            submitButtonText="Update Template"
          />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default EditTemplate; 